import React, { useEffect, useMemo, useState } from "react";
import {
  Col, Row, Card, CardBody, Badge, Button, Modal, ModalHeader, ModalBody,
  ModalFooter, Spinner, Input, Label, FormGroup, Alert
} from "reactstrap";
import TableContainer from "../../../components/Common/TableContainer";
import Breadcrumbs from "../../../components/Common/Breadcrumb";

const BASE_URL = "https://harmanappapi.stechomeyazilim.info";

const EMPTY_FORM = {
  ID: null,
  SAYFA_AD: "",
  ICERIK: "",
};

// Mobil tarafta sık kullanılan sayfa anahtarları (slug önerileri)
const ONERILEN_SAYFALAR = [
  "kullanici-sozlesmesi",
  "gizlilik-politikasi",
  "kvkk-aydinlatma",
  "mesafeli-satis-sozlesmesi",
  "iptal-iade-kosullari",
  "uyelik-kosullari",
  "hakkimizda",
];

const SayfaIcerikleri = () => {
  document.title = "Sayfa İçerikleri | HarmanaGEL";

  const authUser = JSON.parse(localStorage.getItem("authUser") || "{}");
  const token = localStorage.getItem("token") || authUser?.Token;
  const headers = { "Content-Type": "application/json", Authorization: `Bearer ${token}` };

  const [liste, setListe] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState("");

  const [modal, setModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const fetchListe = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const res = await fetch(`${BASE_URL}/SAYFA_ICERIKLERI?$orderby=SAYFA_AD asc`, { headers });
      const result = await res.json();
      setListe(result.value || []);
    } catch {
      setError("Sayfa içerikleri yüklenirken hata oluştu.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => { fetchListe(); }, []);

  const openAdd = () => {
    setFormData(EMPTY_FORM);
    setIsEditing(false);
    setModal(true);
  };

  const handleEdit = (row) => {
    setFormData({
      ID: row.ID,
      SAYFA_AD: row.SAYFA_AD || "",
      ICERIK: row.ICERIK || "",
    });
    setIsEditing(true);
    setModal(true);
  };

  const handleSave = async () => {
    if (!formData.SAYFA_AD.trim() || !formData.ICERIK.trim()) {
      setError("Sayfa adı ve içerik zorunludur.");
      return;
    }
    setIsSaving(true);
    try {
      const payload = {
        SAYFA_AD: formData.SAYFA_AD.trim(),
        ICERIK: formData.ICERIK,
      };

      if (isEditing) {
        await fetch(`${BASE_URL}/SAYFA_ICERIKLERI(${formData.ID})`, {
          method: "PATCH", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Sayfa içeriği güncellendi.");
      } else {
        await fetch(`${BASE_URL}/SAYFA_ICERIKLERI`, {
          method: "POST", headers, body: JSON.stringify(payload),
        });
        setSuccessMsg("Sayfa içeriği eklendi.");
      }

      setModal(false);
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Kayıt sırasında hata oluştu.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu sayfa içeriğini silmek istediğinize emin misiniz?")) return;
    try {
      await fetch(`${BASE_URL}/SAYFA_ICERIKLERI(${id})`, { method: "DELETE", headers });
      setSuccessMsg("Sayfa içeriği silindi.");
      fetchListe();
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch {
      setError("Silme sırasında hata oluştu.");
    }
  };

  const columns = useMemo(() => [
    {
      header: "#",
      accessorKey: "ID",
      enableSorting: true,
      cell: (c) => <strong>#{c.getValue()}</strong>,
    },
    {
      header: "Sayfa Anahtarı",
      accessorKey: "SAYFA_AD",
      enableSorting: true,
      cell: (c) => <Badge color="info" className="font-size-12">{c.getValue() || "---"}</Badge>,
    },
    {
      header: "İçerik (önizleme)",
      accessorKey: "ICERIK",
      enableSorting: false,
      cell: (c) => {
        const text = (c.getValue() || "").replace(/<[^>]+>/g, " ").trim();
        return <span className="text-muted">{text.length > 90 ? text.slice(0, 90) + "…" : (text || "---")}</span>;
      },
    },
    {
      header: "İşlemler",
      enableSorting: false,
      cell: (cellProps) => {
        const row = cellProps.row.original;
        return (
          <div className="d-flex gap-2">
            <Button color="primary" size="sm" outline onClick={() => handleEdit(row)}>
              <i className="bx bx-edit" />
            </Button>
            <Button color="danger" size="sm" outline onClick={() => handleDelete(row.ID)}>
              <i className="bx bx-trash" />
            </Button>
          </div>
        );
      },
    },
  ], []);

  const f = (field, val) => setFormData(p => ({ ...p, [field]: val }));

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="İçerik Yönetimi" breadcrumbItem="Sayfa İçerikleri (Yasal Metinler)" />

          {successMsg && <Alert color="success">{successMsg}</Alert>}
          {error && <Alert color="danger" toggle={() => setError(null)}>{error}</Alert>}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div>
                      <h4 className="card-title mb-1">Sayfa İçerikleri / Yasal Metinler</h4>
                      <p className="text-muted small mb-0">
                        Kullanıcı sözleşmesi, gizlilik politikası, KVKK gibi mobil uygulamada gösterilen metinleri buradan düzenleyin.
                      </p>
                    </div>
                    <Button color="primary" size="sm" onClick={openAdd}>
                      <i className="bx bx-plus me-1" />Yeni Sayfa
                    </Button>
                  </div>

                  {isLoading ? (
                    <div className="text-center py-5"><Spinner color="primary" /></div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={liste}
                      isGlobalFilter={true}
                      isPagination={true}
                      customPageSize={15}
                      SearchPlaceholder="Sayfa ara..."
                      tableClass="align-middle table-nowrap table-hover"
                      theadClass="table-light"
                      paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                      pagination="pagination pagination-rounded justify-content-end mb-2"
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      <Modal isOpen={modal} toggle={() => setModal(false)} centered size="lg">
        <ModalHeader toggle={() => setModal(false)}>
          <i className={`mdi ${isEditing ? "mdi-file-document-edit-outline" : "mdi-file-document-plus-outline"} me-2 text-primary`}></i>
          {isEditing ? `Sayfa Düzenle (#${formData.ID})` : "Yeni Sayfa Ekle"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">Sayfa Anahtarı (slug) <span className="text-danger">*</span></Label>
                <Input
                  value={formData.SAYFA_AD}
                  onChange={(e) => f("SAYFA_AD", e.target.value)}
                  placeholder="Örn: kullanici-sozlesmesi"
                  list="sayfa-onerileri"
                  maxLength={150}
                  disabled={isEditing}
                />
                <datalist id="sayfa-onerileri">
                  {ONERILEN_SAYFALAR.map(s => <option key={s} value={s} />)}
                </datalist>
                <small className="text-muted">
                  Mobil uygulama bu anahtar ile içeriği çağırır. {isEditing && "Düzenlemede değiştirilemez."}
                </small>
              </FormGroup>
            </Col>
            <Col md={12}>
              <FormGroup>
                <Label className="fw-bold">İçerik (HTML destekler) <span className="text-danger">*</span></Label>
                <Input
                  type="textarea"
                  rows={14}
                  value={formData.ICERIK}
                  onChange={(e) => f("ICERIK", e.target.value)}
                  placeholder="Metni veya HTML içeriğini yazın..."
                />
              </FormGroup>
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setModal(false)} disabled={isSaving}>İptal</Button>
          <Button color="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? <Spinner size="sm" className="me-1" /> : <i className="mdi mdi-content-save me-1"></i>}
            {isEditing ? "Güncelle" : "Kaydet"}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default SayfaIcerikleri;
